/**
 * Feature Flags Hook
 * 用於前端檢查租戶啟用的 Feature Flags
 */

import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '../lib/fetcher';

/**
 * 取得租戶啟用的 Feature Flags
 *
 * @param tenantId 租戶 ID（可選，如果不提供則從 localStorage 取得）
 * @returns Feature Flags 相關的狀態和方法
 */
export function useFeatureFlags(tenantId?: string) {
  // 使用 useMemo 穩定 effectiveTenantId，避免不必要的重新渲染
  // 只在 tenantId 參數改變時才重新計算
  const effectiveTenantId = useMemo(() => {
    return (
      tenantId ||
      (typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null)
    );
  }, [tenantId]);

  // 僅在有 token 時才發請求，避免未登入時打 /feature-flags 造成 401 與重試刷屏
  const hasToken =
    typeof window !== 'undefined' &&
    !!(
      localStorage.getItem('staff_session_token') ||
      localStorage.getItem('tenant_token')
    );

  const getAuthFetchOptions = (): RequestInit | undefined => {
    if (typeof window === 'undefined') return undefined;
    const staffToken = localStorage.getItem('staff_session_token');
    const tenantToken = localStorage.getItem('tenant_token');
    const token = staffToken || tenantToken;

    if (!token || !effectiveTenantId) return undefined;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': effectiveTenantId,
      },
    };
  };

  const swrKey =
    effectiveTenantId && hasToken
      ? `/api/tenants/${effectiveTenantId}/feature-flags`
      : null;

  const { data, error, isLoading, mutate } = useSWR<{
    success: boolean;
    data: string[];
  }>(
    swrKey,
    async (url: string): Promise<{ success: boolean; data: string[] }> => {
      const options = getAuthFetchOptions();

      try {
        const result = (await fetcher(url, options)) as {
          success: boolean;
          data: string[];
        };
        return result;
      } catch (err) {
        console.error('[useFeatureFlags] 請求失敗:', {
          url,
          error: err,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    {
      revalidateOnFocus: false, // 關閉視窗焦點重新驗證，避免頻繁請求
      revalidateOnReconnect: false, // 關閉重新連線時重新驗證，避免頻繁請求
      dedupingInterval: 30000, // 增加到 30 秒，大幅減少重複請求
      errorRetryCount: 2, // 減少錯誤重試次數
      errorRetryInterval: 2000, // 增加重試間隔
      // 使用 keepPreviousData 保持舊資料，避免閃爍
      keepPreviousData: true,
    }
  );

  /**
   * 檢查指定功能是否啟用
   *
   * @param featureKey Feature Flag key
   * @returns 是否啟用
   */
  const isEnabled = useCallback(
    (featureKey: string): boolean => {
      if (isLoading) {
        // 載入中時返回 false（保守策略）
        return false;
      }
      if (error) {
        // 有錯誤時返回 false（保守策略）
        console.warn('[useFeatureFlags] 載入錯誤，返回 false:', error);
        return false;
      }
      if (!data?.data) {
        return false;
      }
      return data.data.includes(featureKey);
    },
    [isLoading, error, data]
  );

  /**
   * 批次檢查多個功能是否啟用
   *
   * @param featureKeys Feature Flag keys
   * @returns 功能鍵名與啟用狀態的對應物件
   */
  const checkMultiple = useCallback(
    (featureKeys: string[]): Record<string, boolean> => {
      if (!data?.data) {
        return featureKeys.reduce((acc, key) => ({ ...acc, [key]: false }), {});
      }
      return featureKeys.reduce(
        (acc, key) => ({ ...acc, [key]: data.data.includes(key) }),
        {}
      );
    },
    [data]
  );

  return {
    flags: data?.data ?? [],
    isEnabled,
    checkMultiple,
    isLoading,
    error,
    mutate,
  };
}
